import { z } from 'zod';

import { HoldingUnitEventEnvelopeBaseSchema } from './holding-unit-event-envelope.schema.js';

import {
  HoldingUnitAdjustmentTypeSchema,
  HoldingUnitCapabilitiesSchema,
  HoldingUnitContainerKindSchema,
  HoldingUnitIdentifierSchema,
  HoldingUnitIdentityModeSchema,
  HoldingUnitLifecycleStatusSchema,
  HoldingUnitLoadSessionItemSelectionMethodSchema,
  HoldingUnitLoadSessionStatusSchema,
  HoldingUnitMeasuredDiscrepancyCommittedWeightEventTypeSchema,
  HoldingUnitProductKindSchema,
  HoldingUnitReservationTargetBaseSchema,
  HoldingUnitTypeSchema,
  UnitProfileTemplateKeySnapshotSchema,
  HoldingUnitWeightInputModeSchema,
} from './holding-units.schema.js';
import {
  holdingUnitEventTypeValues,
  type HoldingUnitAdjustmentAppliedEventPayload,
  type HoldingUnitBuildBaleCommittedEventPayload,
  type HoldingUnitCreatedEventPayload,
  type HoldingUnitAndLoadSessionEvent,
  type HoldingUnitStreamEvent,
  type LoadSessionStreamEvent,
  type HoldingUnitIdentifierAssignedEventPayload,
  type HoldingUnitInventoryImpactReversedEventPayload,
  type HoldingUnitLoadSessionCreatedEventPayload,
  type HoldingUnitLoadSessionRenamedEventPayload,
  type HoldingUnitLoadSessionItemAddedEventPayload,
  type HoldingUnitLoadSessionItemMeasuredDiscrepancyCommittedEventPayload,
  type HoldingUnitLoadSessionItemMeasuredDiscrepancyDiscardedEventPayload,
  type HoldingUnitLoadSessionItemRemovedEventPayload,
  type HoldingUnitLoadSessionMaterialGroupCreatedEventPayload,
  type HoldingUnitLoadSessionMaterialGroupUpdatedEventPayload,
  type HoldingUnitLoadSessionStatusChangedEventPayload,
  type HoldingUnitContainerIdentityCorrectedEventPayload,
  type HoldingUnitMaterialCorrectedEventPayload,
  type HoldingUnitMaterialSetEventPayload,
  type HoldingUnitReservationCreatedEventPayload,
  type HoldingUnitReservationReleasedEventPayload,
  type HoldingUnitLifecycleStatusChangedEventPayload,
  type HoldingUnitTareUpdatedEventPayload,
  type HoldingUnitTransferCommittedEventPayload,
  type HoldingUnitWeightAddedEventPayload,
  type HoldingUnitWeightCorrectedEventPayload,
  type HoldingUnitYardLocationMovedEventPayload,
} from './holding-units-events.dto.js';

const timestampSchema = z.iso.datetime();
const auditFlagSchema = z.string().trim().min(1).max(80);

const HoldingUnitAuditWeightBreakdownSchema = z.object({
  grossLb: z.number().nonnegative().nullable(),
  tareLb: z.number().nonnegative().nullable(),
  netLb: z.number().min(0),
});

const HoldingUnitAuditWeightDeltaSchema = z.object({
  grossLb: z.number().nullable(),
  tareLb: z.number().nullable(),
  netLb: z.number(),
});

const optionalReasonAuditFields = {
  reason: z.string().trim().min(1).max(500).nullable().optional(),
  reasonRequired: z.boolean().optional(),
  withinGracePeriod: z.boolean().optional(),
  createdAt: timestampSchema.optional(),
  auditFlags: z.array(auditFlagSchema).optional(),
} as const;

const HoldingUnitEventBaseSchema = HoldingUnitEventEnvelopeBaseSchema.extend({
  entityType: z.literal('holdingUnit'),
}).omit({ eventType: true, payload: true });

const LoadSessionEventBaseSchema = HoldingUnitEventEnvelopeBaseSchema.extend({
  entityType: z.literal('loadSession'),
}).omit({ eventType: true, payload: true });

const HoldingUnitCreatedEventPayloadSchema: z.ZodType<HoldingUnitCreatedEventPayload> = z.object({
  unitType: HoldingUnitTypeSchema,
  identityMode: HoldingUnitIdentityModeSchema,
  containerKind: HoldingUnitContainerKindSchema,
  productKind: HoldingUnitProductKindSchema,
  unitProfileId: z.uuid().nullable().optional(),
  unitProfileRevision: z.number().int().min(1).nullable().optional(),
  unitProfileDisplayName: z.string().trim().min(1).max(120).nullable().optional(),
  unitProfileTemplateKey: UnitProfileTemplateKeySnapshotSchema.nullable().optional(),
  capabilityPolicy: HoldingUnitCapabilitiesSchema.nullable().optional(),
  lifecycleStatus: HoldingUnitLifecycleStatusSchema,
  fixedIdAssetId: z.uuid().nullable().optional(),
});

const HoldingUnitMaterialSetEventPayloadSchema: z.ZodType<HoldingUnitMaterialSetEventPayload> =
  z.object({
    materialId: z.uuid(),
  });

const HoldingUnitMaterialCorrectedEventPayloadSchema: z.ZodType<HoldingUnitMaterialCorrectedEventPayload> =
  z.object({
    previousMaterialId: z.uuid(),
    materialId: z.uuid(),
    previousWeight: HoldingUnitAuditWeightBreakdownSchema.optional(),
    newWeight: HoldingUnitAuditWeightBreakdownSchema.optional(),
    deltaWeight: HoldingUnitAuditWeightDeltaSchema.optional(),
    ...optionalReasonAuditFields,
  });

const HoldingUnitContainerIdentityCorrectedEventPayloadSchema: z.ZodType<HoldingUnitContainerIdentityCorrectedEventPayload> =
  z.object({
    previousAssetId: z.uuid(),
    correctedAssetId: z.uuid(),
    previousBusinessIdentifier: z.string().trim().min(1).max(64),
    correctedBusinessIdentifier: z.string().trim().min(1).max(64),
    previousNormalizedPhysicalId: z.string().trim().min(1).max(64),
    correctedNormalizedPhysicalId: z.string().trim().min(1).max(64),
    previousNormalizedScannableIdentifier: z.string().trim().min(1).max(64),
    correctedNormalizedScannableIdentifier: z.string().trim().min(1).max(64),
    unchangedGrossLb: z.number().nonnegative().nullable(),
    unchangedTareLb: z.number().nonnegative().nullable(),
    unchangedNetLb: z.number().min(0),
    unchangedLifecycleStatus: HoldingUnitLifecycleStatusSchema.extract(['open', 'ready']),
  });

const HoldingUnitIdentifierAssignedEventPayloadSchema: z.ZodType<HoldingUnitIdentifierAssignedEventPayload> =
  z.object({
    identifier: HoldingUnitIdentifierSchema,
    normalizedPhysicalId: z.string().trim().min(1).nullable().optional(),
    normalizedScannableIdentifier: z.string().trim().min(1).nullable().optional(),
  });

const HoldingUnitYardLocationMovedEventPayloadSchema: z.ZodType<HoldingUnitYardLocationMovedEventPayload> =
  z.object({
    yardLocationId: z.uuid().nullable(),
  });

const HoldingUnitTareUpdatedEventPayloadSchema: z.ZodType<HoldingUnitTareUpdatedEventPayload> = z
  .object({
    tareLb: z.number().nonnegative().optional(),
    tareSuggestedLb: z.number().nonnegative().nullable().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.tareLb !== undefined || value.tareSuggestedLb !== undefined) {
      return;
    }
    ctx.addIssue({
      code: 'custom',
      message: 'At least one tare field is required.',
      path: ['tareLb'],
    });
  });

const HoldingUnitWeightAddedEventPayloadSchema: z.ZodType<HoldingUnitWeightAddedEventPayload> =
  z.object({
    inputMode: HoldingUnitWeightInputModeSchema,
    grossTotalLb: z.number().nonnegative().nullable(),
    tareLb: z.number().nonnegative().nullable(),
    netLb: z.number().min(0),
    deltaNetLb: z.number().positive(),
    isManualNetDelta: z.boolean(),
  });

const HoldingUnitWeightCorrectedEventPayloadSchema: z.ZodType<HoldingUnitWeightCorrectedEventPayload> =
  z.object({
    auditBasisEventId: z.uuid().nullable().optional(),
    inputMode: HoldingUnitWeightInputModeSchema,
    grossTotalLb: z.number().nonnegative().nullable(),
    tareLb: z.number().nonnegative().nullable(),
    netLb: z.number().min(0),
    deltaNetLb: z.number(),
    previousWeight: HoldingUnitAuditWeightBreakdownSchema.optional(),
    newWeight: HoldingUnitAuditWeightBreakdownSchema.optional(),
    deltaWeight: HoldingUnitAuditWeightDeltaSchema.optional(),
    ...optionalReasonAuditFields,
  });

const HoldingUnitAdjustmentAppliedEventPayloadSchema: z.ZodType<HoldingUnitAdjustmentAppliedEventPayload> =
  z
    .object({
      adjustmentType: HoldingUnitAdjustmentTypeSchema,
      deltaNetLb: z.number(),
      reason: z.string().trim().min(1).max(500),
    })
    .superRefine((value, ctx) => {
      const isShrink = value.adjustmentType === 'shrink';
      const isValid = isShrink ? value.deltaNetLb < 0 : value.deltaNetLb > 0;

      if (!isValid) {
        ctx.addIssue({
          code: 'custom',
          message: 'deltaNetLb must be negative for shrink and positive for surplus.',
          path: ['deltaNetLb'],
        });
      }
    });

const HoldingUnitTransferCommittedEventPayloadSchema: z.ZodType<HoldingUnitTransferCommittedEventPayload> =
  z
    .object({
      fromUnitId: z.uuid(),
      toUnitId: z.uuid(),
      netLb: z.number().positive(),
      batchId: z.uuid().optional(),
      mode: z.literal('bulk_all_net').optional(),
      lineId: z.uuid().optional(),
      lineIndex: z.number().int().nonnegative().optional(),
      sourceUnitCount: z.number().int().min(2).optional(),
      batchTotalNetLb: z.number().positive().optional(),
      materialId: z.uuid().optional(),
      destinationMaterialAdopted: z.boolean().optional(),
    })
    .superRefine((value, ctx) => {
      const bulkMetadataFields = [
        'batchId',
        'mode',
        'lineId',
        'lineIndex',
        'sourceUnitCount',
        'batchTotalNetLb',
        'materialId',
        'destinationMaterialAdopted',
      ] as const;
      const presentCount = bulkMetadataFields.filter((field) => value[field] !== undefined).length;
      if (presentCount > 0 && presentCount !== bulkMetadataFields.length) {
        ctx.addIssue({
          code: 'custom',
          message: 'Bulk transfer metadata must be complete when present.',
          path: ['batchId'],
        });
      }
    });

const HoldingUnitBuildBaleCommittedEventPayloadSchema: z.ZodType<HoldingUnitBuildBaleCommittedEventPayload> =
  z.object({
    schemaVersion: z.literal(1),
    batchId: z.uuid(),
    stagingUnitId: z.uuid(),
    sourceUnitIds: z.array(z.uuid()),
    productUnitIds: z.array(z.uuid()).min(1),
    leftoverUnitId: z.uuid().nullable().optional(),
    leftoverTargetUnitId: z.uuid().nullable().optional(),
    materialId: z.uuid(),
    sourceNetLb: z.number().min(0),
    productNetLb: z.number().positive(),
    leftoverNetLb: z.number().min(0),
    remainingStagingNetLb: z.number().min(0),
  });

const HoldingUnitInventoryImpactReversedEventPayloadSchema: z.ZodType<HoldingUnitInventoryImpactReversedEventPayload> =
  z.object({
    reason: z.string().trim().min(1).max(500).nullable().optional(),
    reasonRequired: z.boolean(),
    withinGracePeriod: z.boolean(),
    createdAt: timestampSchema,
    removedAt: timestampSchema,
    fromLifecycleStatus: HoldingUnitLifecycleStatusSchema.extract(['open', 'ready']),
    toLifecycleStatus: z.literal('retired'),
    removedWeight: HoldingUnitAuditWeightBreakdownSchema,
    reversalDelta: HoldingUnitAuditWeightDeltaSchema,
    auditFlags: z.array(auditFlagSchema),
  });

const HoldingUnitLifecycleStatusChangedEventPayloadSchema: z.ZodType<HoldingUnitLifecycleStatusChangedEventPayload> =
  z
    .object({
      fromLifecycleStatus: HoldingUnitLifecycleStatusSchema,
      toLifecycleStatus: HoldingUnitLifecycleStatusSchema,
      reason: z.string().trim().min(1).max(500).nullable().optional(),
      shipmentId: z.uuid().nullable().optional(),
      coverShipmentId: z.uuid().nullable().optional(),
      schemaVersion: z.literal(2).optional(),
      shippedNetLb: z.number().positive().nullable().optional(),
      materialId: z.uuid().nullable().optional(),
      isCrvEligible: z.boolean().nullable().optional(),
    })
    .superRefine((value, ctx) => {
      if (value.toLifecycleStatus !== 'shipped') {
        return;
      }

      const outboundRefs = [value.shipmentId, value.coverShipmentId].filter(
        (item): item is string => Boolean(item),
      );
      if (outboundRefs.length !== 1) {
        ctx.addIssue({
          code: 'custom',
          message: 'Exactly one outbound reference is required for shipped status changes.',
          path: ['shipmentId'],
        });
      }
      if (value.schemaVersion !== 2) {
        ctx.addIssue({
          code: 'custom',
          message: 'schemaVersion 2 is required for shipped status changes.',
          path: ['schemaVersion'],
        });
      }
      if (!value.materialId) {
        ctx.addIssue({
          code: 'custom',
          message: 'materialId is required for shipped status changes.',
          path: ['materialId'],
        });
      }
      if (value.isCrvEligible === null || value.isCrvEligible === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'isCrvEligible is required for shipped status changes.',
          path: ['isCrvEligible'],
        });
      }
      if (value.shippedNetLb === null || value.shippedNetLb === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'shippedNetLb is required for shipped status changes.',
          path: ['shippedNetLb'],
        });
      }
    });

const HoldingUnitReservationCreatedEventPayloadSchema: z.ZodType<HoldingUnitReservationCreatedEventPayload> =
  HoldingUnitReservationTargetBaseSchema.extend({
    reservationId: z.uuid(),
  }).superRefine((value, ctx) => {
    const hasLoadSession = Boolean(value.loadSessionId);
    const hasShipment = Boolean(value.shipmentId);

    if (value.reservationType === 'loadSession') {
      if (!hasLoadSession) {
        ctx.addIssue({
          code: 'custom',
          message: 'loadSessionId is required for loadSession reservations.',
          path: ['loadSessionId'],
        });
      }
      if (hasShipment) {
        ctx.addIssue({
          code: 'custom',
          message: 'shipmentId must be empty for loadSession reservations.',
          path: ['shipmentId'],
        });
      }
      return;
    }

    if (!hasShipment) {
      ctx.addIssue({
        code: 'custom',
        message: 'shipmentId is required for shipment reservations.',
        path: ['shipmentId'],
      });
    }
    if (hasLoadSession) {
      ctx.addIssue({
        code: 'custom',
        message: 'loadSessionId must be empty for shipment reservations.',
        path: ['loadSessionId'],
      });
    }
  });

const HoldingUnitReservationReleasedEventPayloadSchema: z.ZodType<HoldingUnitReservationReleasedEventPayload> =
  z.object({
    reservationId: z.uuid(),
  });

const HoldingUnitLoadSessionCreatedEventPayloadSchema: z.ZodType<HoldingUnitLoadSessionCreatedEventPayload> =
  z.object({
    loadSessionId: z.uuid(),
    status: HoldingUnitLoadSessionStatusSchema,
    sessionName: z.string().trim().min(1).max(80).nullable().optional(),
    toleranceProfileKey: z.string().trim().min(1).max(50).nullable().optional(),
  });

const HoldingUnitLoadSessionRenamedEventPayloadSchema: z.ZodType<HoldingUnitLoadSessionRenamedEventPayload> =
  z.object({
    loadSessionId: z.uuid(),
    sessionName: z.string().trim().min(1).max(80).nullable(),
  });

const HoldingUnitLoadSessionStatusChangedEventPayloadSchema: z.ZodType<HoldingUnitLoadSessionStatusChangedEventPayload> =
  z.object({
    loadSessionId: z.uuid(),
    fromStatus: HoldingUnitLoadSessionStatusSchema,
    toStatus: HoldingUnitLoadSessionStatusSchema,
  });

const HoldingUnitLoadSessionMaterialGroupCreatedEventPayloadSchema: z.ZodType<HoldingUnitLoadSessionMaterialGroupCreatedEventPayload> =
  z.object({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    materialId: z.uuid(),
    autoPickEnabled: z.boolean(),
    toleranceProfileKey: z.string().trim().min(1).max(50).nullable(),
    yardLocationIds: z.array(z.uuid()).nullable(),
  });

const HoldingUnitLoadSessionMaterialGroupUpdatedEventPayloadSchema: z.ZodType<HoldingUnitLoadSessionMaterialGroupUpdatedEventPayload> =
  z.object({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    autoPickEnabled: z.boolean(),
    toleranceProfileKey: z.string().trim().min(1).max(50).nullable(),
    yardLocationIds: z.array(z.uuid()).nullable(),
  });

const HoldingUnitLoadSessionItemAddedEventPayloadSchema: z.ZodType<HoldingUnitLoadSessionItemAddedEventPayload> =
  z.object({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    itemId: z.uuid(),
    unitId: z.uuid(),
    selectionMethod: HoldingUnitLoadSessionItemSelectionMethodSchema,
    snapshotNetLbAtSelection: z.number().min(0),
    snapshotGrossLbAtSelection: z.number().min(0).nullable(),
    snapshotTareLbAtSelection: z.number().min(0).nullable(),
    loadMeasuredGrossLb: z.number().min(0).nullable(),
    loadMeasuredNetLb: z.number().min(0).nullable(),
    deltaLossLb: z.number().nullable(),
    toleranceProfileKey: z.string().trim().min(1).max(50).nullable(),
    wasAutoPicked: z.boolean(),
  });

const HoldingUnitLoadSessionItemRemovedEventPayloadSchema: z.ZodType<HoldingUnitLoadSessionItemRemovedEventPayload> =
  z.object({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    itemId: z.uuid(),
  });

const HoldingUnitLoadSessionItemMeasuredDiscrepancyCommittedEventPayloadSchema: z.ZodType<HoldingUnitLoadSessionItemMeasuredDiscrepancyCommittedEventPayload> =
  z.object({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    itemId: z.uuid(),
    unitId: z.uuid(),
    committedWeightEventId: z.uuid(),
    committedWeightEventType: HoldingUnitMeasuredDiscrepancyCommittedWeightEventTypeSchema,
  });

const HoldingUnitLoadSessionItemMeasuredDiscrepancyDiscardedEventPayloadSchema: z.ZodType<HoldingUnitLoadSessionItemMeasuredDiscrepancyDiscardedEventPayload> =
  z.object({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    itemId: z.uuid(),
    unitId: z.uuid(),
  });

const HoldingUnitCreatedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitCreated'),
  payload: HoldingUnitCreatedEventPayloadSchema,
});

const HoldingUnitMaterialSetEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitMaterialSet'),
  payload: HoldingUnitMaterialSetEventPayloadSchema,
});

const HoldingUnitMaterialCorrectedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitMaterialCorrected'),
  payload: HoldingUnitMaterialCorrectedEventPayloadSchema,
});

const HoldingUnitContainerIdentityCorrectedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitContainerIdentityCorrected'),
  payload: HoldingUnitContainerIdentityCorrectedEventPayloadSchema,
});

const HoldingUnitIdentifierAssignedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitIdentifierAssigned'),
  payload: HoldingUnitIdentifierAssignedEventPayloadSchema,
});

const HoldingUnitYardLocationMovedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitYardLocationMoved'),
  payload: HoldingUnitYardLocationMovedEventPayloadSchema,
});

const HoldingUnitTareUpdatedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitTareUpdated'),
  payload: HoldingUnitTareUpdatedEventPayloadSchema,
});

const HoldingUnitWeightAddedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitWeightAdded'),
  payload: HoldingUnitWeightAddedEventPayloadSchema,
});

const HoldingUnitWeightCorrectedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitWeightCorrected'),
  payload: HoldingUnitWeightCorrectedEventPayloadSchema,
});

const HoldingUnitAdjustmentAppliedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitAdjustmentApplied'),
  payload: HoldingUnitAdjustmentAppliedEventPayloadSchema,
});

const HoldingUnitTransferCommittedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitTransferCommitted'),
  payload: HoldingUnitTransferCommittedEventPayloadSchema,
});

const HoldingUnitBuildBaleCommittedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitBuildBaleCommitted'),
  payload: HoldingUnitBuildBaleCommittedEventPayloadSchema,
});

const HoldingUnitInventoryImpactReversedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitInventoryImpactReversed'),
  payload: HoldingUnitInventoryImpactReversedEventPayloadSchema,
});

const HoldingUnitLifecycleStatusChangedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitStatusChanged'),
  payload: HoldingUnitLifecycleStatusChangedEventPayloadSchema,
});

const HoldingUnitReservationCreatedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitReservationCreated'),
  payload: HoldingUnitReservationCreatedEventPayloadSchema,
});

const HoldingUnitReservationReleasedEventSchema = HoldingUnitEventBaseSchema.extend({
  eventType: z.literal('holdingUnitReservationReleased'),
  payload: HoldingUnitReservationReleasedEventPayloadSchema,
});

const HoldingUnitLoadSessionCreatedEventSchema = LoadSessionEventBaseSchema.extend({
  eventType: z.literal('holdingUnitLoadSessionCreated'),
  payload: HoldingUnitLoadSessionCreatedEventPayloadSchema,
});

const HoldingUnitLoadSessionRenamedEventSchema = LoadSessionEventBaseSchema.extend({
  eventType: z.literal('holdingUnitLoadSessionRenamed'),
  payload: HoldingUnitLoadSessionRenamedEventPayloadSchema,
});

const HoldingUnitLoadSessionStatusChangedEventSchema = LoadSessionEventBaseSchema.extend({
  eventType: z.literal('holdingUnitLoadSessionStatusChanged'),
  payload: HoldingUnitLoadSessionStatusChangedEventPayloadSchema,
});

const HoldingUnitLoadSessionMaterialGroupCreatedEventSchema = LoadSessionEventBaseSchema.extend({
  eventType: z.literal('holdingUnitLoadSessionMaterialGroupCreated'),
  payload: HoldingUnitLoadSessionMaterialGroupCreatedEventPayloadSchema,
});

const HoldingUnitLoadSessionMaterialGroupUpdatedEventSchema = LoadSessionEventBaseSchema.extend({
  eventType: z.literal('holdingUnitLoadSessionMaterialGroupUpdated'),
  payload: HoldingUnitLoadSessionMaterialGroupUpdatedEventPayloadSchema,
});

const HoldingUnitLoadSessionItemAddedEventSchema = LoadSessionEventBaseSchema.extend({
  eventType: z.literal('holdingUnitLoadSessionItemAdded'),
  payload: HoldingUnitLoadSessionItemAddedEventPayloadSchema,
});

const HoldingUnitLoadSessionItemRemovedEventSchema = LoadSessionEventBaseSchema.extend({
  eventType: z.literal('holdingUnitLoadSessionItemRemoved'),
  payload: HoldingUnitLoadSessionItemRemovedEventPayloadSchema,
});

const HoldingUnitLoadSessionItemMeasuredDiscrepancyCommittedEventSchema =
  LoadSessionEventBaseSchema.extend({
    eventType: z.literal('holdingUnitLoadSessionItemMeasuredDiscrepancyCommitted'),
    payload: HoldingUnitLoadSessionItemMeasuredDiscrepancyCommittedEventPayloadSchema,
  });

const HoldingUnitLoadSessionItemMeasuredDiscrepancyDiscardedEventSchema =
  LoadSessionEventBaseSchema.extend({
    eventType: z.literal('holdingUnitLoadSessionItemMeasuredDiscrepancyDiscarded'),
    payload: HoldingUnitLoadSessionItemMeasuredDiscrepancyDiscardedEventPayloadSchema,
  });

export const HoldingUnitStreamEventSchema: z.ZodType<HoldingUnitStreamEvent> = z.discriminatedUnion(
  'eventType',
  [
    HoldingUnitCreatedEventSchema,
    HoldingUnitMaterialSetEventSchema,
    HoldingUnitMaterialCorrectedEventSchema,
    HoldingUnitContainerIdentityCorrectedEventSchema,
    HoldingUnitIdentifierAssignedEventSchema,
    HoldingUnitYardLocationMovedEventSchema,
    HoldingUnitTareUpdatedEventSchema,
    HoldingUnitWeightAddedEventSchema,
    HoldingUnitWeightCorrectedEventSchema,
    HoldingUnitAdjustmentAppliedEventSchema,
    HoldingUnitTransferCommittedEventSchema,
    HoldingUnitBuildBaleCommittedEventSchema,
    HoldingUnitInventoryImpactReversedEventSchema,
    HoldingUnitLifecycleStatusChangedEventSchema,
    HoldingUnitReservationCreatedEventSchema,
    HoldingUnitReservationReleasedEventSchema,
  ],
);

export const LoadSessionStreamEventSchema: z.ZodType<LoadSessionStreamEvent> = z.discriminatedUnion(
  'eventType',
  [
    HoldingUnitLoadSessionCreatedEventSchema,
    HoldingUnitLoadSessionRenamedEventSchema,
    HoldingUnitLoadSessionStatusChangedEventSchema,
    HoldingUnitLoadSessionMaterialGroupCreatedEventSchema,
    HoldingUnitLoadSessionMaterialGroupUpdatedEventSchema,
    HoldingUnitLoadSessionItemAddedEventSchema,
    HoldingUnitLoadSessionItemRemovedEventSchema,
    HoldingUnitLoadSessionItemMeasuredDiscrepancyCommittedEventSchema,
    HoldingUnitLoadSessionItemMeasuredDiscrepancyDiscardedEventSchema,
  ],
);

export const HoldingUnitAndLoadSessionEventSchema: z.ZodType<HoldingUnitAndLoadSessionEvent> =
  z.union([HoldingUnitStreamEventSchema, LoadSessionStreamEventSchema]);

export const HoldingUnitEventTypeSchema = z.enum(holdingUnitEventTypeValues);
